import { useEditorStore } from "../../../shared/stores/useEditorStore";
import apiService from "../../../services/ApiService";
import type { TreeItem, ScenarioState, Sensitivity, SensitivityModelData, PatchTreeUpdater } from "../types";
import { mapModelData } from "../utils/treeUtils";

interface UseNodeExpandParams {
  item: TreeItem;
  variant: string;
  currentSensitivity: Sensitivity | 'null' | null;
  patchTree: (updater: PatchTreeUpdater) => void;
}

export function useNodeExpand({
  item,
  variant,
  currentSensitivity,
  patchTree,
}: UseNodeExpandParams) {
  const handleToggleExpand = async () => {
    const key = item.kind + item.id.toString();
    const isCurrentlyExpanded =
      useEditorStore.getState().expandedNodes.has(key);
    const newExpanded = !isCurrentlyExpanded;

    useEditorStore.getState().setNodeExpanded(key, newExpanded);

    let loadedChildren: Record<string, ReturnType<typeof mapModelData>> | null =
      null;

    if (newExpanded && item.childrens.length > 0 && variant === "Input") {
      let needsLoad = false;
      patchTree((prev) => {
        // Check ALL children, not just the first — search may have merged a sparse
        // subset (only matched siblings + their ancestors), so a "first child loaded"
        // heuristic would skip the fetch and leave siblings invisible.
        const allLoaded = item.childrens.every(
          (id: string) => !!prev.Input.tree.items[id],
        );
        if (!allLoaded) {
          needsLoad = true;
        }
        return prev;
      });

      if (needsLoad) {
        useEditorStore.getState().setNodeLoading(key, true);
        patchTree((prev) => ({
          ...prev,
          Input: {
            ...prev.Input,
            tree: {
              ...prev.Input.tree,
              items: { ...prev.Input.tree.items },
            },
          },
        }));

        try {
          const response = await apiService.getModelDataChildren(
            parseInt(item.id),
          );
          if (response?.ok) {
            const children = await response.json();
            loadedChildren = {};
            for (const child of children) {
              loadedChildren[child.id.toString()] = mapModelData(child);
            }

            // 'null' (a string) is how the editor says "no sensitivity".
            if (currentSensitivity && currentSensitivity !== 'null') {
              try {
                const sensResponse = await apiService.getSensitivity(
                  currentSensitivity.id,
                );
                if (sensResponse?.ok) {
                  const sensitivity = await sensResponse.json();
                  sensitivity.model_datas?.forEach(
                    (sObj: SensitivityModelData) => {
                      const childItem =
                        loadedChildren?.[
                          sObj.original_model_data_id?.toString() ?? ""
                        ];
                      if (childItem) {
                        childItem.sensitivityModelData = sObj;
                      }
                    },
                  );
                }
              } catch (sensErr) {
                console.error(
                  "Failed to apply sensitivity on children:",
                  sensErr,
                );
              }
            }
          }
        } catch (err) {
          console.error("Failed to load children:", err);
        }
        useEditorStore.getState().setNodeLoading(key, false);
      }
    }

    patchTree((prev) => {
      const newInputItems = loadedChildren
        ? { ...prev.Input.tree.items, ...loadedChildren }
        : { ...prev.Input.tree.items };
      return {
        ...prev,
        Input: {
          ...prev.Input,
          tree: { ...prev.Input.tree, items: newInputItems },
        },
        Output: {
          ...prev.Output,
          tree: {
            ...prev.Output.tree,
            items: { ...prev.Output.tree.items },
          },
        },
      };
    });
  };

  return { handleToggleExpand };
}
